import HighlightIcon from "@assets/highlight-unselected.png";
import { css, useTheme } from "@emotion/react";
import AnnotationIcon from "@mui/icons-material/StickyNote2TwoTone";
import { useMediaQuery } from "@mui/material";
import type { Annotation } from "@rowan/prisma";
import React from "react";
import { mixHighlightColour } from "../../design/colours";
import { getAnnotationId, shallowEqual, shallowEqualArray } from "../utils";
import { HighlightButton } from "./buttons/highlightButton";
import {
  InlineAnnotationMenu,
  StyledInlineAnnotationMenu,
} from "./InlineAnnotationMenu";
import { responsiveMargins } from "./layout/cssHelpers";
import { pageColourOptions } from "./settings/defaults";

const SmallHighlightMenu = ({
  annotation,
  focusedId,
  setFocusedId,
  annotationPositions,
  pageColour,
}: {
  annotation: Annotation;
  focusedId?: string;
  setFocusedId: (id?: string) => void;
  annotationPositions: Record<string, { x: number; y: number }>;
  pageColour?: string;
}) => {
  const theme = useTheme();
  const smallPos = annotationPositions[`sm${annotation.id}`];
  const isDark = pageColour === pageColourOptions.black.background;
  const annotationId = getAnnotationId(annotation);
  const baseColour = isDark
    ? focusedId === annotationId
      ? pageColour
      : theme.colours.accent.light._15
    : pageColour || theme.colours.accent.light._15;

  const mixAmount =
    focusedId === annotationId ? (isDark ? 60 : 90) : isDark ? 90 : undefined;

  const colour = mixHighlightColour(
    baseColour,
    theme.colours.highlight.yellow,
    mixAmount,
  );
  return (
    <div key={`sm-${annotation.id}`}>
      <div
        id={`sm-${annotation.id}`}
        css={({ colours }) => css`
          height: 30px;
          width: 30px;
          border-radius: 50%;
          border: 1px solid ${colours.highlight.yellow};
          background-color: white;
          color: ${colours.uIGrey.dark._10};
          display: flex;
          justify-content: center;
          align-items: center;
          :hover {
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
          }
        `}
        style={{
          position: "absolute",
          left: (smallPos?.x ?? 0) - 3,
          top: smallPos?.y ?? 0,
          visibility: smallPos?.x ? "visible" : "hidden",
        }}
      >
        <HighlightButton
          isSaved={false}
          onClick={() => {
            setFocusedId(focusedId === annotationId ? undefined : annotationId);
          }}
          style={{ height: "30px", width: "30px" }}
          colour={colour}
        >
          {annotation.noteText ? (
            <AnnotationIcon sx={{ height: "16px" }} />
          ) : (
            <img
              src={HighlightIcon}
              alt="Select annotation"
              title="Select annotation"
              css={css`
                // to override the article image styling
                height: 20px !important;
              `}
            />
          )}
        </HighlightButton>
      </div>
    </div>
  );
};

type ChunkProps = {
  position: string;
  html: string;
  currentChunk?: string;
  focusedId?: string;
  annotations: Annotation[];
  annotationPositions: Record<string, { x: number; y: number }>;
  annotationsOpen?: boolean;
  clearHighlight: (id: string) => void;
  applyBorder: (range: Range, id: string) => void;
  clearBorder: (id?: string) => void;
  deserializeRange: (annotation: Annotation["range"]) => Range | null;
  textContainerRef: React.RefObject<HTMLElement | null>;
  setFocusedId: (id?: string) => void;
  pageColour?: string;
};

const ChunkDisplay = React.forwardRef<HTMLDivElement, ChunkProps>(
  (
    {
      position,
      html,
      currentChunk,
      focusedId,
      annotations,
      annotationPositions,
      annotationsOpen,
      clearHighlight,
      applyBorder,
      clearBorder,
      deserializeRange,
      setFocusedId,
      pageColour,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(
      `(max-width: ${theme.breakpoints.values.lg}px)`,
    );
    const hide = currentChunk !== undefined && +position < +currentChunk;

    return (
      <>
        <div
          ref={ref}
          data-chunk={position}
          id={`chunk-${position}`}
          style={{
            ...(hide ? { visibility: "hidden" } : {}),
            ...(annotationsOpen && !isSmallScreen
              ? { width: "calc(100% - 270px)" }
              : { width: "100%" }),
            paddingBottom: "11rem",
          }}
          css={(theme) => responsiveMargins({ theme })}
          dangerouslySetInnerHTML={{ __html: html }}
        />

        {annotationsOpen &&
          annotations.map((annotation) => {
            const annotationId = getAnnotationId(annotation);
            const pos = annotationPositions[`${annotation.id}`];

            return (
              <div key={`menu-${annotationId}`}>
                <SmallHighlightMenu
                  key={`sm-${annotation.id}`}
                  focusedId={focusedId}
                  setFocusedId={setFocusedId}
                  annotationPositions={annotationPositions}
                  annotation={annotation}
                  pageColour={pageColour}
                />
                <StyledInlineAnnotationMenu
                  key={annotation.id}
                  id={`ann${annotation.id}`}
                  smallScreen={isSmallScreen}
                  focused={focusedId === annotationId}
                  onClick={() => setFocusedId(annotationId)}
                  onBlur={() => {
                    setFocusedId(undefined);
                  }}
                  style={{
                    position: "absolute",
                    left: pos?.x ?? 0,
                    top: pos?.y ?? 0,
                    visibility: pos?.x ? "visible" : "hidden",
                    ...(isSmallScreen && focusedId !== annotationId
                      ? { visibility: "hidden", display: "none" }
                      : {}),
                    ...(isSmallScreen && focusedId === annotationId
                      ? {
                          top: (pos?.y ?? 0) - 70,
                          left: (pos?.x ?? 0) - 300,
                        }
                      : {}),
                  }}
                >
                  <InlineAnnotationMenu
                    annotation={annotation}
                    clearHighlight={clearHighlight}
                    clearBorder={clearBorder}
                    deserializeRange={deserializeRange}
                    applyBorder={applyBorder}
                  />
                </StyledInlineAnnotationMenu>
              </div>
            );
          })}
      </>
    );
  },
);

export const Chunk = React.memo(
  ChunkDisplay,
  (prev: ChunkProps, next: ChunkProps): boolean => {
    return (
      prev.html === next.html &&
      prev.currentChunk === next.currentChunk &&
      prev.focusedId === next.focusedId &&
      prev.annotationsOpen === next.annotationsOpen &&
      shallowEqual(prev.annotationPositions, next.annotationPositions) &&
      shallowEqualArray(prev.annotations, next.annotations)
    );
  },
);
